import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Quote } from "lucide-react";
import Reveal from "@/components/site/Reveal";
import { CALENDLY_URL, CLIENTS, PROBLEMS, PROGRAMMES, TESTIMONIALS, STATS, WHY, METHOD, IMG } from "@/data/content";

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="pt-36 lg:pt-44 pb-20 lg:pb-28 bg-gwa-bg relative overflow-hidden">
        <div className="gwa-container grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="gwa-eyebrow"><span className="gwa-rule" />Executive Leadership Advisory for Women</p>
            </Reveal>
            <Reveal delay={120}>
              <h1 className="gwa-display text-[2.6rem] sm:text-6xl lg:text-[5rem] mt-6 text-gwa-primary leading-[1.02]">
                Turning expertise<br />
                into <em className="italic font-light text-gwa-purple">leadership.</em>
              </h1>
            </Reveal>
            <Reveal delay={240}>
              <p className="mt-9 text-lg lg:text-xl text-gwa-muted-fg max-w-2xl leading-relaxed">
                Leadership programmes, coaching, and behavioural simulations designed to help high-performing women step into leadership with confidence, influence, and impact.
              </p>
            </Reveal>
            <Reveal delay={360}>
              <div className="mt-12 flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid="hero-cta-pipeline" className="gwa-btn-primary">
                  Discuss your leadership pipeline <ArrowUpRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 relative">
            <Reveal delay={300}>
              <div className="relative aspect-[3/4] overflow-hidden bg-gwa-secondary">
                <img
                  src={IMG.hero}
                  alt="Pascale Lebaillif, founder of Global Women Ascend"
                  className="w-full h-full object-cover"
                  data-testid="hero-founder-image"
                />
                <div className="absolute inset-0 ring-1 ring-inset ring-gwa-purple/30 pointer-events-none" />
              </div>
              <div className="mt-5 flex items-baseline justify-between">
                <p className="font-serif text-2xl text-gwa-primary">Pascale Lebaillif</p>
                <p className="text-xs uppercase tracking-[0.18em] text-gwa-muted-fg">Founder</p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE BRIEF WE KEEP HEARING — moved directly under hero */}
      <section className="bg-gwa-ivory border-y border-gwa-border">
        <div className="gwa-container py-20 lg:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <Reveal>
                <p className="gwa-eyebrow"><span className="gwa-rule" />The brief we keep hearing</p>
                <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary leading-[1.05]">
                  The pipeline tightens<br />where it matters most.
                </h2>
              </Reveal>
              <Reveal delay={120}>
                <p className="mt-7 text-gwa-muted-fg leading-relaxed">
                  We hear the same pattern from HR Directors, Heads of Talent, and business leaders. Capability is not the issue. Translation into senior leadership is.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-gwa-border">
              {PROBLEMS.map((p, i) => (
                <Reveal key={p.quote} delay={80 * i} className="bg-gwa-ivory p-8 lg:p-10">
                  <p className="font-serif italic text-2xl lg:text-[1.6rem] text-gwa-primary leading-snug">
                    “{p.quote}”
                  </p>
                  <p className="mt-5 text-sm text-gwa-muted-fg leading-relaxed">{p.note}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP — dark credibility band */}
      <section data-testid="trust-strip" className="bg-[#651A75] text-gwa-bg gwa-noise relative">
        <div className="gwa-container py-14 lg:py-16 relative z-10">
          <p className="gwa-eyebrow-light text-center mb-8">Selected engagements & advisory</p>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-5">
            {CLIENTS.map((c) => (
              <span key={c} className="font-serif text-xl lg:text-2xl text-gwa-bg/85 tracking-tight">
                {c}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* METHOD */}
      <section className="gwa-section bg-gwa-bg" data-testid="method-section">
        <div className="gwa-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="gwa-eyebrow"><span className="gwa-rule" />{METHOD.eyebrow}</p>
                <h2 className="gwa-display text-4xl lg:text-[3.25rem] mt-6 text-gwa-primary leading-[1.05]">
                  {METHOD.headline}
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-7">
              <Reveal delay={120}>
                <div className="space-y-5 text-lg text-gwa-primary/85 leading-relaxed font-light">
                  {METHOD.intro.split("\n\n").map((para, i) => (
                    <p key={i}>{para}</p>
                  ))}
                </div>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-gwa-border">
            {METHOD.steps.map((s, i) => (
              <Reveal key={s.n} delay={80 * i} className="bg-gwa-bg p-8 lg:p-10 flex flex-col gap-4">
                <p className="font-serif text-5xl text-gwa-purple leading-none">{s.n}</p>
                <h3 className="font-serif text-2xl text-gwa-primary leading-snug">{s.title}</h3>
                <p className="text-gwa-muted-fg leading-relaxed text-[0.95rem]">{s.body}</p>
              </Reveal>
            ))}
          </div>

          <Reveal delay={240}>
            <p className="mt-14 font-serif italic text-2xl text-gwa-primary max-w-3xl leading-snug">
              {METHOD.closing}
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHY — Pascale's authority */}
      <section className="bg-[#651A75] text-gwa-bg gwa-noise" data-testid="why-section">
        <div className="gwa-container py-24 lg:py-32 relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="relative bg-white/5 aspect-[4/5] overflow-hidden">
                <img
                  src={IMG.cutout}
                  alt="Pascale Lebaillif"
                  className="absolute inset-0 w-full h-full object-cover object-top"
                  data-testid="why-image"
                />
                <span className="absolute -bottom-2 -left-2 w-24 h-px bg-gwa-gold" />
                <span className="absolute -top-2 -right-2 w-24 h-px bg-gwa-bg" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal>
              <p className="gwa-eyebrow-light"><span className="gwa-rule" />{WHY.eyebrow}</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-[2.85rem] mt-6 leading-[1.1]">
                {WHY.headline}
              </h2>
            </Reveal>
            <div className="mt-8 space-y-5 text-lg text-gwa-bg/85 leading-[1.7] font-light max-w-2xl">
              {WHY.body.map((p, i) => (
                <Reveal key={i} delay={80 * i}>
                  <p>{p}</p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={300}>
              <p className="mt-8 font-serif italic text-gwa-gold text-lg">{WHY.signature}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THREE OFFERS — outcome-led */}
      <section className="gwa-section bg-gwa-bg" id="offers">
        <div className="gwa-container">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <Reveal>
                <p className="gwa-eyebrow"><span className="gwa-rule" />How we engage</p>
                <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary">Three engagements. One outcome.</h2>
              </Reveal>
            </div>
            <Reveal delay={120}>
              <Link to="/programmes" data-testid="home-cta-programmes" className="gwa-btn-ghost self-start">
                See full programmes <ArrowRight className="w-4 h-4" />
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {/* Featured — DARK */}
            <Reveal className="lg:col-span-7 bg-[#651A75] text-gwa-bg p-10 lg:p-14 flex flex-col justify-between min-h-[480px]">
              <div>
                <p className="gwa-eyebrow-light">{PROGRAMMES[0].eyebrow}</p>
                <h3 className="font-serif text-4xl lg:text-5xl mt-5 leading-tight">{PROGRAMMES[0].title}</h3>
                <p className="font-serif italic text-xl text-gwa-gold mt-4 leading-snug">{PROGRAMMES[0].subtitle}</p>
                <p className="mt-6 text-gwa-bg/85 text-lg max-w-xl leading-relaxed">{PROGRAMMES[0].headlineOutcome}</p>
              </div>
              <div className="mt-10 flex flex-col sm:flex-row sm:items-end justify-between gap-6">
                <p className="text-sm uppercase tracking-[0.18em] text-gwa-gold">{PROGRAMMES[0].format}</p>
                <Link to="/programmes#expert-to-leader" data-testid="offer-card-expert" className="gwa-btn-ghost text-gwa-bg border-gwa-bg">
                  Programme detail <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>

            <Reveal delay={120} className="lg:col-span-5 bg-gwa-secondary p-10 lg:p-12 flex flex-col justify-between min-h-[480px] hover:-translate-y-1 transition-transform duration-500">
              <div>
                <p className="gwa-eyebrow">{PROGRAMMES[1].eyebrow}</p>
                <h3 className="font-serif text-3xl lg:text-4xl mt-5 leading-tight text-gwa-primary">{PROGRAMMES[1].title}</h3>
                <p className="font-serif italic text-lg text-gwa-purple mt-3 leading-snug">{PROGRAMMES[1].subtitle}</p>
                <p className="mt-5 text-gwa-muted-fg leading-relaxed">{PROGRAMMES[1].headlineOutcome}</p>
              </div>
              <div className="mt-8">
                <p className="text-xs uppercase tracking-[0.18em] text-gwa-primary/70 mb-4">{PROGRAMMES[1].format}</p>
                <Link to="/programmes#leadership-coaching" data-testid="offer-card-coaching" className="gwa-btn-ghost">
                  Programme detail <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>

            <Reveal delay={180} className="lg:col-span-12 bg-gwa-bg border border-gwa-border p-10 lg:p-14 grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
              <div className="lg:col-span-7">
                <p className="gwa-eyebrow">{PROGRAMMES[2].eyebrow}</p>
                <h3 className="font-serif text-3xl lg:text-5xl mt-5 leading-tight text-gwa-primary">{PROGRAMMES[2].title}</h3>
                <p className="font-serif italic text-lg text-gwa-purple mt-3 leading-snug">{PROGRAMMES[2].subtitle}</p>
                <p className="mt-5 text-gwa-muted-fg leading-relaxed max-w-2xl">{PROGRAMMES[2].headlineOutcome}</p>
              </div>
              <div className="lg:col-span-5 lg:text-right">
                <p className="text-xs uppercase tracking-[0.18em] text-gwa-primary/70 mb-4">{PROGRAMMES[2].format}</p>
                <Link to="/programmes#atlantis" data-testid="offer-card-atlantis" className="gwa-btn-ghost">
                  Programme detail <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CREDIBILITY STATS */}
      <section className="border-y border-gwa-border bg-gwa-ivory">
        <div className="gwa-container py-20 lg:py-24">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
            {STATS.map((s, i) => (
              <Reveal key={s.label} delay={80 * i}>
                <p className="gwa-stat text-6xl lg:text-7xl text-gwa-primary">{s.value}</p>
                <p className="mt-3 text-sm tracking-wider uppercase text-gwa-muted-fg">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS — outcome-led pull quotes */}
      <section className="gwa-section bg-gwa-bg">
        <div className="gwa-container">
          <Reveal>
            <p className="gwa-eyebrow"><span className="gwa-rule" />Outcomes, in their words</p>
            <h2 className="gwa-display text-4xl lg:text-5xl mt-6 text-gwa-primary max-w-3xl">
              The shift is measurable.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 lg:gap-14 mt-16">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={t.author} delay={80 * i}>
                <article className="border-l-2 border-gwa-accent pl-7 py-2">
                  <Quote className="w-5 h-5 text-gwa-accent mb-4" />
                  <p className="font-serif italic text-2xl lg:text-[1.65rem] text-gwa-primary leading-snug">
                    “{t.pull}”
                  </p>
                  <p className="mt-5 text-gwa-muted-fg leading-relaxed text-[0.95rem]">{t.quote}</p>
                  <div className="mt-6">
                    <p className="font-medium text-gwa-primary">{t.author}</p>
                    <p className="text-sm text-gwa-muted-fg tracking-wide">{t.role}</p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FOUNDER FEATURE — authority-led */}
      <section className="bg-gwa-secondary">
        <div className="gwa-container py-24 lg:py-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="aspect-[4/5] overflow-hidden bg-gwa-bg">
                <img src={IMG.navy} alt="Pascale Lebaillif" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={120}>
              <p className="gwa-eyebrow"><span className="gwa-rule" />Insider expertise</p>
              <h2 className="gwa-display text-4xl lg:text-[3.25rem] mt-6 text-gwa-primary leading-[1.05]">
                "I've sat on the committees<br />where careers are decided."
              </h2>
              <p className="mt-8 text-lg text-gwa-muted-fg leading-relaxed max-w-2xl">
                Twenty years inside Morgan Stanley, Credit Suisse, and J.P. Morgan, running global teams and deciding senior progression from inside the room. The work is informed by being on both sides of the table: as the senior leader navigating power, and as the committee member assessing readiness. Direct, commercially grounded, and unsentimental about what senior leadership actually requires.
              </p>
              <div className="mt-10">
                <Link to="/about" data-testid="home-cta-about" className="gwa-btn-secondary">
                  Read full background <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="bg-gwa-bg">
        <div className="gwa-container py-24 lg:py-32 text-center">
          <Reveal>
            <p className="gwa-eyebrow justify-center"><span className="gwa-rule" />Engage</p>
            <h2 className="gwa-display text-4xl sm:text-5xl lg:text-6xl mt-7 text-gwa-primary max-w-4xl mx-auto leading-[1.05]">
              Discuss your leadership pipeline.
            </h2>
            <p className="mt-8 text-gwa-muted-fg max-w-3xl mx-auto leading-relaxed text-lg">
              I work with organisations where high-performing women are not translating into senior leaders, and fix the gap between delivery and influence.
            </p>
            <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" data-testid="closing-cta-pipeline" className="gwa-btn-primary">
                Discuss your leadership pipeline <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
